import { ReactNode, useRef, useEffect, useCallback, useMemo, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui';

import { useKeyboardListNavigation } from '@backrs_ui/grow-ui/src/hooks/useKeyboardListNavigation';

import { Box, CircularProgress, Typography } from '@mui/material';

import {
  contentSx,
  loadingContainerSx,
  emptyStateContainerSx,
  sectionTitleSx,
} from './CommandPalette.styles';
import { CommandPaletteSection } from './CommandPalette.types';
import { CommandPaletteItem } from './CommandPaletteItem';

export interface CommandPaletteListProps<T> {
  items: T[];
  sections?: CommandPaletteSection<T>[];
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  getItemKey: (item: T) => string;
  selectedItem: T | null;
  onItemSelect: (item: T) => void;
  onClose: () => void;
  loading?: boolean;
  emptyStateMessage?: string;
  onKeyDown?: (handler: (event: React.KeyboardEvent) => void) => void;
}

export const CommandPaletteList = <T,>({
  items,
  sections,
  renderItem,
  getItemKey,
  selectedItem,
  onItemSelect,
  onClose,
  loading = false,
  emptyStateMessage = 'No results found',
  onKeyDown: externalOnKeyDown,
}: CommandPaletteListProps<T>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  // Get display items - either from sections or direct items array
  const displayItems = useMemo(() => {
    if (sections && sections.length > 0) {
      return sections.flatMap((section) => section.items);
    }

    return items;
  }, [items, sections]);

  // Use keyboard navigation hook
  const { selectedIndex, setSelectedIndex, handleKeyDown } = useKeyboardListNavigation(
    displayItems,
    onItemSelect,
    onClose,
    { wrap: true, initialIndex: 0 },
  );

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0);
    setHoveredIndex(-1);
  }, [displayItems, setSelectedIndex]);

  // Call external onKeyDown handler if provided
  useEffect(() => {
    if (externalOnKeyDown) {
      externalOnKeyDown(handleKeyDown);
    }
  }, [externalOnKeyDown, handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  const handleItemClick = useCallback(
    (item: T, index: number): void => {
      setSelectedIndex(index);
      onItemSelect(item);
    },
    [onItemSelect, setSelectedIndex],
  );

  const handleItemHover = useCallback(
    (item: T): void => {
      const index = displayItems.findIndex(
        (displayItem) => getItemKey(displayItem) === getItemKey(item),
      );

      setHoveredIndex(index);
    },
    [displayItems, getItemKey],
  );

  const handleMouseLeave = useCallback((): void => {
    setHoveredIndex(-1);
  }, []);

  const renderContent = (): ReactNode => {
    if (loading) {
      return (
        <Box sx={loadingContainerSx}>
          <CircularProgress size={24} />
          <Typography variant="body2">{appStrings.assigneeEmulation.loadingText}</Typography>
        </Box>
      );
    }

    if (displayItems.length === 0) {
      return (
        <Box sx={emptyStateContainerSx}>
          <Typography variant="body2" color="text.secondary">
            {emptyStateMessage}
          </Typography>
        </Box>
      );
    }

    if (sections && sections.length > 0) {
      let itemIndex = 0;

      return sections.map((section, sectionIndex) => (
        <Box key={sectionIndex}>
          {section.title && (
            <Typography variant="overline" sx={sectionTitleSx}>
              {section.title}
            </Typography>
          )}
          {section.items.map((item) => {
            const currentIndex = itemIndex++;
            const isKeyboardSelected = currentIndex === selectedIndex;
            const isHovered = currentIndex === hoveredIndex;
            const isSelected = Boolean(
              selectedItem && getItemKey(item) === getItemKey(selectedItem),
            );
            const hasFocus = (isKeyboardSelected || isHovered) && !isSelected;

            return (
              <CommandPaletteItem
                key={getItemKey(item)}
                item={item}
                isSelected={isSelected}
                hasFocus={hasFocus}
                renderItem={renderItem}
                onItemSelect={() => handleItemClick(item, currentIndex)}
                onItemHover={handleItemHover}
              />
            );
          })}
        </Box>
      ));
    }

    return displayItems.map((item, index) => {
      const isKeyboardSelected = index === selectedIndex;
      const isHovered = index === hoveredIndex;
      const isSelected = Boolean(selectedItem && getItemKey(item) === getItemKey(selectedItem));
      const hasFocus = (isKeyboardSelected || isHovered) && !isSelected;

      return (
        <CommandPaletteItem
          key={getItemKey(item)}
          item={item}
          isSelected={isSelected}
          hasFocus={hasFocus}
          renderItem={renderItem}
          onItemSelect={() => handleItemClick(item, index)}
          onItemHover={handleItemHover}
        />
      );
    });
  };

  return (
    <Box ref={listRef} sx={contentSx} onMouseLeave={handleMouseLeave}>
      {renderContent()}
    </Box>
  );
};
